import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

export interface FormMultiSelectOption {
  value: string;
  label: string;
  group?: string; // Optional group label for grouped display
  subLabel?: string; // Optional secondary label
}

interface FormMultiSelectProps {
  label: string;
  options: FormMultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  error?: string;
  helpText?: string;
  searchable?: boolean;
  grouped?: boolean;
}

export function FormMultiSelect({
  label,
  options,
  selected,
  onChange,
  placeholder = 'Select...',
  error,
  helpText,
  searchable = false,
  grouped = false,
}: FormMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropdownPos, setDropdownPos] = useState({ top: 0, bottom: 0, left: 0, width: 0, openUpward: false });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const MAX_DROPDOWN_HEIGHT = 256; // max-h-64 = 16rem = 256px

  // Calculate dropdown position — flip upward when not enough space below
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 4;
    const spaceAbove = rect.top - 4;
    const openUpward = spaceBelow < MAX_DROPDOWN_HEIGHT && spaceAbove > spaceBelow;
    setDropdownPos({
      top: openUpward ? 0 : rect.bottom + 4,
      bottom: openUpward ? window.innerHeight - rect.top + 4 : 0,
      left: rect.left,
      width: rect.width,
      openUpward,
    });
  }, []);

  // Position dropdown when opened, reposition on scroll/resize
  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  const handleToggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const handleRemove = (value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selected.filter(v => v !== value));
  };

  // Filter options based on search
  const filteredOptions = searchable && search
    ? options.filter(
        (opt) =>
          opt.label.toLowerCase().includes(search.toLowerCase()) ||
          opt.group?.toLowerCase().includes(search.toLowerCase()) ||
          opt.subLabel?.toLowerCase().includes(search.toLowerCase())
      )
    : options;

  // Group options if grouped prop is true
  const groupedOptions = grouped
    ? filteredOptions.reduce((groups, option) => {
        const group = option.group || 'Other';
        if (!groups[group]) {
          groups[group] = [];
        }
        groups[group].push(option);
        return groups;
      }, {} as Record<string, FormMultiSelectOption[]>)
    : null;

  // Get selected option labels for display
  const selectedLabels = selected
    .map((value) => options.find((o) => o.value === value)?.label)
    .filter(Boolean);

  // Dropdown content rendered via portal
  const dropdownContent = isOpen && createPortal(
    <div
      ref={dropdownRef}
      className="bg-background-elevated border border-border-default rounded-md shadow-lg max-h-64 overflow-auto"
      style={{
        position: 'fixed',
        ...(dropdownPos.openUpward
          ? { bottom: dropdownPos.bottom, top: 'auto' }
          : { top: dropdownPos.top, bottom: 'auto' }),
        left: dropdownPos.left,
        width: dropdownPos.width,
        zIndex: 9999,
      }}
    >
      {/* Search Input */}
      {searchable && (
        <div className="p-2 border-b border-border-subtle">
          <input
            ref={searchInputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="w-full px-2 py-1 text-sm border border-border-default rounded focus:outline-none focus:border-blue-500"
          />
        </div>
      )}

      {/* Grouped Options */}
      {grouped && groupedOptions ? (
        Object.entries(groupedOptions).map(([group, opts]) => (
          <div key={group}>
            <div className="px-3 py-1.5 text-xs font-semibold text-text-secondary uppercase bg-surface-subtle sticky top-0">
              {group}
            </div>
            {opts.map((option) => {
              const isSelected = selected.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleToggle(option.value)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-surface-hover"
                >
                  <div
                    className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                      isSelected
                        ? 'bg-blue-600 border-blue-600'
                        : 'border-border-default'
                    }`}
                  >
                    {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-text-primary truncate">{option.label}</div>
                    {option.subLabel && (
                      <div className="text-xs text-text-tertiary truncate">
                        {option.subLabel}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        ))
      ) : (
        /* Flat Options */
        filteredOptions.map((option) => {
          const isSelected = selected.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => handleToggle(option.value)}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-surface-hover"
            >
              <div
                className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                  isSelected
                    ? 'bg-blue-600 border-blue-600'
                    : 'border-border-default'
                }`}
              >
                {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
              </div>
              <span className="text-text-primary">{option.label}</span>
            </button>
          );
        })
      )}

      {filteredOptions.length === 0 && (
        <div className="px-3 py-4 text-sm text-text-secondary text-center">
          {search ? 'No matches found' : 'No options available'}
        </div>
      )}
    </div>,
    document.body
  );

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-text-primary">
        {label}
      </label>

      <div className="relative">
        {/* Trigger Button */}
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm border rounded-md transition-colors bg-background-primary text-left min-h-[38px] ${
            error
              ? 'border-red-500 focus:ring-red-500'
              : 'border-border-default hover:border-border-focus focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
          }`}
        >
          <div className="flex-1 flex flex-wrap gap-1">
            {selected.length === 0 ? (
              <span className="text-text-tertiary">{placeholder}</span>
            ) : (
              selectedLabels.map((label, idx) => (
                <span
                  key={selected[idx]}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs"
                >
                  {label}
                  <XMarkIcon
                    className="w-3 h-3 cursor-pointer hover:text-blue-600"
                    onClick={(e) => handleRemove(selected[idx], e)}
                  />
                </span>
              ))
            )}
          </div>
          <ChevronDownIcon
            className={`w-4 h-4 text-text-tertiary flex-shrink-0 transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        {/* Dropdown rendered via portal */}
        {dropdownContent}
      </div>

      {helpText && !error && (
        <p className="text-xs text-text-secondary">{helpText}</p>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
